
export type ConnectionQuality =
  | 'new'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed'
  | 'closed';

export interface PeerSignals {
  sendOffer(sdp: string): void;
  sendAnswer(sdp: string): void;
  sendCandidate(candidate: RTCIceCandidateInit): void;
}

export interface PeerOptions extends PeerSignals {
  polite: boolean;
  iceServers: RTCIceServer[];
  onRemoteStream(stream: MediaStream): void;
  onQualityChange(quality: ConnectionQuality): void;
  forceRelay?: boolean;
  onNegotiated?(pc: RTCPeerConnection): void;
}

export class Peer {
  readonly pc: RTCPeerConnection;
  #polite: boolean;
  #makingOffer = false;
  #ignoreOffer = false;
  #closed = false;
  #opts: PeerOptions;

  constructor(opts: PeerOptions) {
    this.#opts = opts;
    this.#polite = opts.polite;
    this.pc = new RTCPeerConnection({
      iceServers: opts.iceServers,
      iceTransportPolicy: opts.forceRelay ? 'relay' : 'all',
      iceCandidatePoolSize: 1,
      bundlePolicy: 'max-bundle',
    });

    this.pc.onnegotiationneeded = async () => {
      try {
        this.#makingOffer = true;
        await this.pc.setLocalDescription();
        const sdp = this.pc.localDescription?.sdp;
        if (sdp) opts.sendOffer(sdp);
      } catch (err) {
        console.error('negotiation failed', err);
      } finally {
        this.#makingOffer = false;
      }
    };

    this.pc.onicecandidate = ({ candidate }) => {
      if (candidate) opts.sendCandidate(candidate.toJSON());
    };

    this.pc.ontrack = ({ track, streams }) => {
      const stream = streams[0];
      if (stream) opts.onRemoteStream(stream);
      track.onunmute = () => {
        if (stream) opts.onRemoteStream(stream);
      };
    };

    this.pc.onconnectionstatechange = () => this.#reportQuality();
    this.pc.oniceconnectionstatechange = () => {
      if (this.pc.iceConnectionState === 'failed') this.pc.restartIce();
      this.#reportQuality();
    };
  }

  #reportQuality(): void {
    if (this.#closed) return;
    const state = this.pc.connectionState;
    const map: Record<RTCPeerConnectionState, ConnectionQuality> = {
      new: 'new',
      connecting: 'connecting',
      connected: 'connected',
      disconnected: 'reconnecting',
      failed: 'failed',
      closed: 'closed',
    };
    this.#opts.onQualityChange(map[state] ?? 'connecting');
  }

  addLocalStream(stream: MediaStream): void {
    for (const track of stream.getTracks()) this.pc.addTrack(track, stream);
  }

  async onDescription(type: 'offer' | 'answer', sdp: string): Promise<void> {
    const description = { type, sdp } as RTCSessionDescriptionInit;

    const offerCollision =
      type === 'offer' && (this.#makingOffer || this.pc.signalingState !== 'stable');

    this.#ignoreOffer = !this.#polite && offerCollision;
    if (this.#ignoreOffer) return;

    await this.pc.setRemoteDescription(description);

    if (type === 'offer') {
      await this.pc.setLocalDescription();
      const answer = this.pc.localDescription?.sdp;
      if (answer) this.#opts.sendAnswer(answer);
    }
    this.#opts.onNegotiated?.(this.pc);
  }

  async onCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    try {
      await this.pc.addIceCandidate(candidate);
    } catch (err) {
      if (!this.#ignoreOffer) console.warn('could not add ICE candidate', err);
    }
  }

  restart(): void {
    if (this.#closed) return;
    this.pc.restartIce();
  }

  close(): void {
    this.#closed = true;
    this.pc.onnegotiationneeded = null;
    this.pc.onicecandidate = null;
    this.pc.ontrack = null;
    this.pc.onconnectionstatechange = null;
    this.pc.oniceconnectionstatechange = null;
    this.pc.close();
  }
}
